import * as React from "react";
import {
    displayPhoneNumber,
    formatLegacyPhoneNumber,
} from "../../../../../utils/utils";
import styles from "./CommunicationPhoneLink.module.scss";

export type RingCentralDestination =
    | "dialer"
    | "new-sms"
    | "call-history"
    | "sms-history";

interface CommunicationPhoneLinkProps {
    value?: string;
    destination?: Extract<RingCentralDestination, "dialer" | "new-sms">;
}

export function getRingCentralUrl(
    value: string,
    destination: RingCentralDestination
): string | null {
    const normalizedPhoneNumber = formatLegacyPhoneNumber(value);
    const digits = normalizedPhoneNumber.replace(/\D/g, "");
    if (!normalizedPhoneNumber.startsWith("+1") || digits.length !== 11) {
        return null;
    }

    const destinations: Record<RingCentralDestination, string> = {
        dialer: `/dialer?number=${digits}`,
        "new-sms": `/sms?type=new&number=${digits}`,
        "call-history": `/history?number=${digits}`,
        "sms-history": `/sms?number=${digits}`,
    };

    return `https://app.ringcentral.com/r${destinations[destination]}`;
}

export function getRingCentralDialerUrl(value: string): string | null {
    return getRingCentralUrl(value, "dialer");
}

export default function CommunicationPhoneLink({
    value,
    destination = "dialer",
}: CommunicationPhoneLinkProps) {
    if (!value) {
        return <span>--</span>;
    }

    const ringCentralUrl = getRingCentralUrl(value, destination);
    const displayValue = displayPhoneNumber(value);
    if (!ringCentralUrl) {
        return <span>{displayValue}</span>;
    }

    const isNewSms = destination === "new-sms";

    return (
        <a
            href={ringCentralUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.phoneLink}
            aria-label={`${
                isNewSms ? "Text" : "Call"
            } ${displayValue} in RingCentral`}
            title={`Open RingCentral ${isNewSms ? "new message" : "dialer"}`}
            onClick={(event) => event.stopPropagation()}
        >
            {displayValue}
        </a>
    );
}
